if (!customElements.get('product-media-gallery')) {
  customElements.define(
    'product-media-gallery',
    class ProductMediaGallery extends HTMLElement {
      resizeObserver = null;

      constructor() {
        super();

        this.sliderElement = this.querySelector('.product-gallery-main-slider');
        this.sliderThumbnailElement = this.querySelector('.product-gallery-thumbnail-slider');
        this.nextButton = this.querySelector('.product-gallery-main .swiper-button-next');
        this.previousButton = this.querySelector('.product-gallery-main .swiper-button-prev');

        this.mainSlider = null;
        this.thumbnailSlider = null;

        this.mainSliderSettings = this.getMainSliderSettings();
        this.mainSliderOptions = this.getMainSliderOptions();
      }

      connectedCallback() {
        if (!this.sliderElement) {
          console.warn('Missing main slider element');
          return;
        }

        this.initSliders();
        this.observeResize();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        window.visualViewport?.removeEventListener('resize', this.onGalleryResize);
        this.mainSlider?.destroy();
        this.thumbnailSlider?.destroy();
      }

      getMainSliderSettings() {
        try {
          return JSON.parse(this.dataset.sliderConfiguration || '{}');
        } catch (e) {
          console.warn('Invalid main slider configuration JSON', e);
          return {};
        }
      }

      getMainSliderOptions() {
        return {
          initialSlide: this.mainSliderSettings.initialSlide,
          effect: 'fade',
          fadeEffect: { crossFade: true },
          autoHeight: true,
          slidesPerView: 1,
          loop: this.mainSliderSettings.enableLoop || false,
          speed: this.mainSliderSettings.speed || 300,
          autoplay: Shopify.designMode
            ? false
            : this.mainSliderSettings.enableAutoplay
            ? {
                delay: this.mainSliderSettings.autoplayDelay || 7000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false,
          navigation:
            this.mainSliderSettings.enableNavigation && this.nextButton && this.previousButton
              ? {
                  nextEl: this.nextButton,
                  prevEl: this.previousButton,
                }
              : false,
          keyboard: { enabled: true, onlyInViewport: true },
          a11y: {
            enabled: true,
            prevSlideMessage: 'Previous slide',
            nextSlideMessage: 'Next slide',
            firstSlideMessage: 'This is the first slide',
            lastSlideMessage: 'This is the last slide',
            paginationBulletMessage: 'Go to slide {{index}}',
          },
          threshold: 5,
          longSwipesRatio: 0.05,
          longSwipesMs: 500,
        };
      }

      getThumbnailSliderOptions() {
        return {
          initialSlide: this.mainSliderSettings.initialSlide,
          loop: true,
          spaceBetween: 0,
          freeMode: true,
          slidesPerView: 'auto',
          navigation: false,
          slideToClickedSlide: true,
        };
      }

      initSliders() {
        if (this.mainSliderSettings.enableThumbnail && this.sliderThumbnailElement) {
          this.thumbnailSlider = new Swiper(this.sliderThumbnailElement, this.getThumbnailSliderOptions());

          this.mainSliderOptions.thumbs = {
            swiper: this.thumbnailSlider,
          };
        }

        this.mainSlider = new Swiper(this.sliderElement, this.mainSliderOptions);

        this.mainSlider.on('slideChange', () => {
          this.mainSlider.updateAutoHeight();

          if (this.thumbnailSlider) {
            const realIndex = this.mainSlider.realIndex;
            const targetSlide = this.thumbnailSlider.slides[realIndex];
            if (targetSlide) {
              const offset = targetSlide.offsetLeft * -1;
              this.thumbnailSlider.translateTo(offset, this.mainSliderSettings.speed || 300);
            }
          }
        });

        this.sliderElement.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', () => this.mainSlider?.updateAutoHeight(), { once: true });
          }
        });

        requestAnimationFrame(() => this.mainSlider?.updateAutoHeight());
      }

      bindVariantChange(index) {
        if (!this.mainSlider) return;
        this.mainSlider.slideTo(index, this.mainSliderSettings.speed || 300);
      }

      refreshGalleryLayout() {
        this.mainSlider?.updateAutoHeight?.();
        this.mainSlider?.update?.();
        this.thumbnailSlider?.update?.();
      }

      observeResize() {
        if (!this.mainSlider) return;

        this.onGalleryResize = () => this.refreshGalleryLayout();

        window.visualViewport?.addEventListener('resize', this.onGalleryResize);

        if (!window.ResizeObserver) return;

        this.resizeObserver = new ResizeObserver(this.onGalleryResize);
        this.resizeObserver.observe(this.sliderElement);
      }
    }
  );
}
